import { FC, useState, useEffect } from 'react';
import { Box, Typography, Paper, Alert, Skeleton } from '@mui/material';
import { Preference } from '@libs/models';
import { useGetUserPreferencesQuery, useUpdateUserPreferencesMutation } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';
import { useAuth } from '@carcareers/hooks';
import { AuthRole } from '@libs/types';
import { EmployerPreferences } from './EmployerPreferences';
import { JobseekerPreferences } from './JobseekerPreferences';

export const UserPreferences: FC = () => {
  const auth = useAuth();
  const userRole = auth.session?.user?.role;
  const { data: preferences, isLoading, error } = useGetUserPreferencesQuery();
  const [updatePreferences, { isLoading: isUpdating }] = useUpdateUserPreferencesMutation();
  const { toggleSnackbar } = useSnackbar();

  const [localPreferences, setLocalPreferences] = useState<Partial<Preference>>({});

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleToggle = async (field: keyof Preference, value: boolean) => {
    const updatedPreferences = { ...localPreferences, [field]: value };
    setLocalPreferences(updatedPreferences);

    try {
      await updatePreferences({ [field]: value }).unwrap();
      toggleSnackbar('success', 'Notification preferences updated');
    } catch (err) {
      toggleSnackbar('error', 'Failed to update preferences');
      // Revert on error
      setLocalPreferences(localPreferences);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load notification preferences. Please try again later.</Alert>;
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 0.5 }}>
          Notification Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage which notifications you want to receive via email.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {userRole === AuthRole.EMPLOYER && (
          <EmployerPreferences
            localPreferences={localPreferences}
            handleToggle={handleToggle}
            isUpdating={isUpdating}
          />
        )}
        {userRole === AuthRole.JOBSEEKER && (
          <JobseekerPreferences
            localPreferences={localPreferences}
            handleToggle={handleToggle}
            isUpdating={isUpdating}
          />
        )}
      </Box>

      {isUpdating && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          Updating preferences...
        </Typography>
      )}
    </Paper>
  );
};
